import { Controller } from "@hotwired/stimulus"

/**
 * Phase 2.9 — Approver-pool picker modal.
 *
 * Listens for `pool-picker:open` from the workflow builder. Five tabs:
 *   - Existing Pool   → pick from the company's already-saved pools (default)
 *   - Company Role    → select of CompanyRole names on the company
 *   - Specific Users  → multi-select of company users
 *   - Project Field   → drop-down of whitelisted Project user-pointer columns
 *   - Top-Level Role  → radio of commander / captain / regional_access
 *
 * "Existing Pool" dispatches `pool-picker:selected` directly with the
 * chosen pool id (no POST). The four create-new tabs persist via the
 * `quick_create` endpoint and dispatch the resulting row.
 */
export default class extends Controller {
  static targets = ["panel", "name", "namePanel", "tab", "tabContent", "errors", "saveButton"]
  static values = {
    quickCreateUrl: String,
    companyRoles: Array,
    topRoles: Array,
    topRoleNames: Object,
    projectFields: Array,
    existingPools: Array,
    companyUsers: Array
  }

  connect() {
    this._activeTab = "existing"
    this._stepId = null
    this._tabState = {
      existing:      { pool_id: "" },
      company_role:  { name: "" },
      named_user:    { user_ids: [] },
      project_field: { field: (this.projectFieldsValue && this.projectFieldsValue[0]) || "" },
      user_in_role_within_company: { top_role: (this.topRolesValue && this.topRolesValue[0]) || "captain" }
    }

    this._onOpen = this._onOpen.bind(this)
    document.addEventListener("pool-picker:open", this._onOpen)
    this._renderTabContent()
    this._syncNamePanel()
  }

  disconnect() {
    document.removeEventListener("pool-picker:open", this._onOpen)
  }

  _onOpen(event) {
    this._stepId = event.detail?.stepId || null
    this.element.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  hide() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  selectTab(event) {
    this._activeTab = event.currentTarget.dataset.tab
    this._renderTabContent()
    this._syncNamePanel()
    if (this.hasTabTarget) {
      this.tabTargets.forEach(t => {
        const active = t.dataset.tab === this._activeTab
        t.classList.toggle("border-blue-500", active)
        t.classList.toggle("text-blue-600", active)
        t.classList.toggle("font-semibold", active)
        t.classList.toggle("border-transparent", !active)
        t.classList.toggle("text-gray-500", !active)
      })
    }
  }

  _syncNamePanel() {
    if (!this.hasNamePanelTarget) return
    this.namePanelTarget.classList.toggle("hidden", this._activeTab === "existing")
  }

  _renderTabContent() {
    if (!this.hasTabContentTarget) return
    let html = ""
    switch (this._activeTab) {
      case "existing": {
        const pools = this.existingPoolsValue || []
        const cur = this._tabState.existing.pool_id
        if (pools.length === 0) {
          html = `<div class="text-[13px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            No pools saved yet. Use one of the other tabs to define a new pool inline,
            or visit Settings → Approvals → Pools to build one.
          </div>`
        } else {
          html = `
            <label class="block text-sm font-medium text-gray-700 mb-1">Saved pool</label>
            <select class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white cursor-pointer"
                    data-action="change->approver-pool-picker#updateExistingPool">
              <option value="">— select —</option>
              ${pools.map(p => `<option value="${p.id}" ${String(p.id) === String(cur) ? "selected" : ""}>${this._escape(p.name)} — ${this._humanize(p.kind)}${p.summary ? `: ${this._escape(p.summary)}` : ""}</option>`).join("")}
            </select>
            <p class="text-[11px] text-gray-500 mt-1">Reuse a pool defined elsewhere — updates to the pool flow through to every step that references it.</p>`
        }
        break
      }
      case "company_role": {
        const roles = this.companyRolesValue || []
        const cur = this._tabState.company_role.name
        html = `
          <label class="block text-sm font-medium text-gray-700 mb-1">Company role</label>
          <select class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white cursor-pointer"
                  data-action="change->approver-pool-picker#updateCompanyRole">
            <option value="">— select —</option>
            ${roles.map(r => `<option value="${this._escape(r)}" ${r === cur ? "selected" : ""}>${this._escape(r)}</option>`).join("")}
          </select>
          <p class="text-[11px] text-gray-500 mt-1">Resolved at decision time: every project-member with this role title.</p>`
        break
      }
      case "named_user": {
        const users = this.companyUsersValue || []
        const cur = (this._tabState.named_user.user_ids || []).map(String)
        if (users.length === 0) {
          html = `<div class="text-[13px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            No company users to pick from.
          </div>`
        } else {
          html = `
            <label class="block text-sm font-medium text-gray-700 mb-1">Users</label>
            <select multiple size="8"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                    data-action="change->approver-pool-picker#updateNamedUsers">
              ${users.map(u => `<option value="${u.id}" ${cur.includes(String(u.id)) ? "selected" : ""}>${this._escape(u.name)}</option>`).join("")}
            </select>
            <p class="text-[11px] text-gray-500 mt-1">Hold ⌘/Ctrl to select multiple. Resolves to exactly these users on every request.</p>`
        }
        break
      }
      case "project_field": {
        const fields = this.projectFieldsValue || []
        const cur = this._tabState.project_field.field
        html = `
          <label class="block text-sm font-medium text-gray-700 mb-1">Project user-pointer column</label>
          <select class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white cursor-pointer"
                  data-action="change->approver-pool-picker#updateProjectField">
            ${fields.map(f => `<option value="${this._escape(f)}" ${f === cur ? "selected" : ""}>${this._escape(this._humanize(f.replace(/_id$/, "")))}</option>`).join("")}
          </select>
          <p class="text-[11px] text-gray-500 mt-1">Resolves to the user pointed at by this column on the request's project.</p>`
        break
      }
      case "user_in_role_within_company": {
        const tops = this.topRolesValue || []
        const cur = this._tabState.user_in_role_within_company.top_role
        html = `
          <label class="block text-sm font-medium text-gray-700 mb-1">Top-level company role</label>
          <div class="flex flex-col gap-1">
            ${tops.map(r => `<label class="inline-flex items-center gap-2 text-sm">
              <input type="radio" name="top_role" value="${this._escape(r)}" ${r === cur ? "checked" : ""}
                data-action="change->approver-pool-picker#updateTopRole" />
              ${this._escape((this.topRoleNamesValue || {})[r] || this._humanize(r))}
            </label>`).join("")}
          </div>`
        break
      }
    }
    this.tabContentTarget.innerHTML = html
  }

  updateExistingPool(e) { this._tabState.existing.pool_id = e.target.value }
  updateCompanyRole(e)  { this._tabState.company_role.name = e.target.value }
  updateNamedUsers(e) {
    const select = e.target
    this._tabState.named_user.user_ids = Array.from(select.selectedOptions).map(o => parseInt(o.value, 10)).filter(Boolean)
  }
  updateProjectField(e) { this._tabState.project_field.field = e.target.value }
  updateTopRole(e)      { this._tabState.user_in_role_within_company.top_role = e.target.value }

  async save() {
    // ── Existing pool tab — no POST, just dispatch the chosen id. ──
    if (this._activeTab === "existing") {
      const poolId = this._tabState.existing.pool_id
      if (!poolId) {
        this._showError("Pick a saved pool")
        return
      }
      const pool = (this.existingPoolsValue || []).find(p => String(p.id) === String(poolId))
      document.dispatchEvent(new CustomEvent("pool-picker:selected", {
        detail: { poolId: parseInt(poolId, 10), pool: pool, stepId: this._stepId }
      }))
      this.hide()
      return
    }

    // ── Create-new flow — POST to quick_create, then dispatch. ──
    const config = this._buildConfig()
    if (!config) return

    const name = this.hasNameTarget ? this.nameTarget.value.trim() : ""
    if (!name) {
      this._showError("Pool name is required")
      return
    }

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const r = await fetch(this.quickCreateUrlValue, {
      method: "POST",
      headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf || "" },
      body: JSON.stringify({ pool: { name: name, kind: this._activeTab, config_json: config } })
    })
    const j = await r.json().catch(() => ({}))
    if (j.success) {
      document.dispatchEvent(new CustomEvent("pool-picker:selected", {
        detail: { poolId: j.pool.id, pool: j.pool, stepId: this._stepId }
      }))
      this.hide()
    } else {
      this._showError((j.errors || ["Failed to create pool"]).join(", "))
    }
  }

  _buildConfig() {
    switch (this._activeTab) {
      case "company_role": {
        if (!this._tabState.company_role.name) {
          this._showError("Pick a company role")
          return null
        }
        return { company_role_name: this._tabState.company_role.name }
      }
      case "named_user": {
        const ids = (this._tabState.named_user.user_ids || []).map(Number).filter(Boolean)
        if (!ids.length) {
          this._showError("Pick at least one user")
          return null
        }
        return { user_ids: ids }
      }
      case "project_field": {
        if (!this._tabState.project_field.field) {
          this._showError("Pick a project field")
          return null
        }
        return { project_field: this._tabState.project_field.field }
      }
      case "user_in_role_within_company": {
        if (!this._tabState.user_in_role_within_company.top_role) {
          this._showError("Pick a top-level role")
          return null
        }
        return { top_role: this._tabState.user_in_role_within_company.top_role }
      }
    }
  }

  _showError(msg) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
    setTimeout(() => this.errorsTarget.classList.add("hidden"), 4000)
  }

  _escape(str) {
    if (str === null || str === undefined) return ""
    return String(str).replace(/[&<>"']/g, m => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;"
    })[m])
  }

  _humanize(s) {
    if (!s) return ""
    return String(s).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }
}
